import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import { fetchWithAuth } from '../utils/api';
import '../styles/GitHubConnect.css';

function GitHubConnect({ problemId, onConnect, currentRepoUrl }) {
  const [githubStatus, setGithubStatus] = useState(null);
  const [repositories, setRepositories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRepo, setSelectedRepo] = useState('');
  const [customRepoUrl, setCustomRepoUrl] = useState('');
  const [useCustomUrl, setUseCustomUrl] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const checkGitHubStatus = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/github/status`);
      if (response.ok) {
        const data = await response.json();
        setGithubStatus(data);
        return data.connected;
      }
      return false;
    } catch (err) {
      console.error('Error checking GitHub status:', err);
      return false;
    }
  }, []);

  const fetchRepositories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/github/repositories`);

      if (!response.ok) {
        throw new Error('Failed to fetch repositories');
      }

      const data = await response.json();
      setRepositories(data.repositories || []);
    } catch (err) {
      console.error('Error fetching repositories:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const isConnected = await checkGitHubStatus();
      if (isConnected) {
        await fetchRepositories();
      } else {
        setLoading(false);
      }
    };
    init();
  }, [checkGitHubStatus, fetchRepositories]);

  const handleConnect = async () => {
    const repoUrl = useCustomUrl ? customRepoUrl : selectedRepo;

    if (!repoUrl) {
      setError('Please select or enter a repository URL');
      return;
    }

    try {
      setConnecting(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_BASE_URL}/github/problems/${problemId}/connect`,
        {
          method: 'POST',
          body: JSON.stringify({ github_repo_url: repoUrl })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to connect repository');
      }

      const result = await response.json();

      if (onConnect) {
        onConnect(result);
      }
    } catch (err) {
      console.error('Error connecting repository:', err);
      setError(err.message);
    } finally {
      setConnecting(false);
    }
  };

  const filteredRepos = repositories.filter(repo =>
    repo.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (repo.description && repo.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (!githubStatus?.connected) {
    return (
      <div className="github-connect not-connected">
        <div className="connection-prompt">
          <span className="github-logo">🔗</span>
          <h3>Connect Your GitHub Account</h3>
          <p>
            To read and push changes to repositories, you need to sign in with GitHub
            and grant repository access.
          </p>
          <p className="hint">
            Sign out and sign in again with GitHub to grant repository permissions.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="github-connect loading">
        <div className="loading-spinner"></div>
        <p>Loading repositories...</p>
      </div>
    );
  }

  return (
    <div className="github-connect">
      {/* Connected GitHub Account Info */}
      <div className="github-account-info">
        <img
          src={githubStatus.github_user.avatar_url}
          alt={githubStatus.github_user.login}
          className="avatar"
        />
        <div className="account-details">
          <span className="username">{githubStatus.github_user.login}</span>
          {githubStatus.github_user.name && (
            <span className="name">{githubStatus.github_user.name}</span>
          )}
        </div>
        <span className="connected-badge">Connected</span>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {currentRepoUrl && (
        <div className="current-repo-info">
          <span className="label">Currently Connected:</span>
          <span className="repo-url">{currentRepoUrl}</span>
        </div>
      )}

      {/* Repository Selection */}
      <div className="repo-selection">
        <div className="selection-tabs">
          <button
            className={`tab ${!useCustomUrl ? 'active' : ''}`}
            onClick={() => setUseCustomUrl(false)}
          >
            My Repositories
          </button>
          <button
            className={`tab ${useCustomUrl ? 'active' : ''}`}
            onClick={() => setUseCustomUrl(true)}
          >
            Custom URL
          </button>
        </div>

        {!useCustomUrl ? (
          <div className="repo-list-section">
            <div className="search-input">
              <input
                type="text"
                placeholder="Search repositories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="repo-list">
              {filteredRepos.length === 0 ? (
                <div className="no-repos">
                  {searchQuery ? 'No repositories match your search' : 'No repositories found'}
                </div>
              ) : (
                filteredRepos.map((repo) => (
                  <div
                    key={repo.id}
                    className={`repo-item ${selectedRepo === repo.url ? 'selected' : ''}`}
                    onClick={() => setSelectedRepo(repo.url)}
                  >
                    <div className="repo-main">
                      <input
                        type="radio"
                        name="repository"
                        checked={selectedRepo === repo.url}
                        onChange={() => setSelectedRepo(repo.url)}
                      />
                      <div className="repo-info">
                        <span className="repo-name">
                          {repo.private && <span className="private-icon">🔒</span>}
                          {repo.full_name}
                        </span>
                        {repo.description && (
                          <span className="repo-description">{repo.description}</span>
                        )}
                      </div>
                    </div>
                    <div className="repo-meta">
                      <span className="default-branch">{repo.default_branch}</span>
                      {repo.permissions?.push && (
                        <span className="push-access">Push Access</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="custom-url-section">
            <label htmlFor="custom-url">GitHub Repository URL</label>
            <input
              id="custom-url"
              type="url"
              placeholder="https://github.com/owner/repo"
              value={customRepoUrl}
              onChange={(e) => setCustomRepoUrl(e.target.value)}
            />
            <p className="hint">
              Enter the URL of a public repository or a private repository you have access to.
            </p>
          </div>
        )}
      </div>

      {/* Connect Button */}
      <div className="connect-actions">
        <button
          className="connect-btn"
          onClick={handleConnect}
          disabled={connecting || (!useCustomUrl && !selectedRepo) || (useCustomUrl && !customRepoUrl)}
        >
          {connecting ? (
            <>
              <span className="loading-spinner small"></span>
              Connecting...
            </>
          ) : currentRepoUrl ? (
            'Update Repository'
          ) : (
            'Connect Repository'
          )}
        </button>
      </div>

      {/* Info about what happens */}
      <div className="connect-info">
        <h4>What happens when you connect?</h4>
        <ul>
          <li>A working branch will be created for this project</li>
          <li>You'll be able to browse repository files</li>
          <li>Agent changes can be committed and pushed</li>
          <li>You can create pull requests from the working branch</li>
        </ul>
      </div>
    </div>
  );
}

export default GitHubConnect;
